import axios from "axios";
import config from "./config";

/**
 * Destination-company (company B) readiness, checked ONCE when B is picked rather
 * than discovered at commit.
 *
 * B must already have a `billing_information` row: the backend refuses a Customer
 * Switch with 409 CUSTOMER_SWITCH_NO_BILLING at BOTH preview and commit, and it
 * deliberately will not fabricate a `billing_id` (that sequence is Laravel's).
 * Without this precheck every point in the group fails identically at the last step.
 *
 * 🔴 This endpoint — NOT `/company/getUserCompany`. Both would answer the question,
 * but the company thunk's `fulfilled` reducer writes `state.selectedCompany` and
 * `localStorage["selectedUserCompany"]`, so resolving B through it would silently
 * switch the whole application from A to B mid-flow. This call touches no store.
 */
export interface DestinationReadiness {
  /**
   * Whether B has billing information.
   *
   * `undefined` = the backend does not report it yet (the field is additive and
   * ships with PR A). Treat undefined as UNKNOWN and allow the user to continue —
   * the backend's own 409 is the real gate. Treat an explicit `false` as a block.
   * Do not collapse the two: assuming "absent means missing" blocks every company
   * until PR A lands, and assuming "absent means fine" hides a real refusal after.
   */
  hasBilling: boolean | undefined;
  /** Company B's contact email — the proposal recipient. B's, never company A's. */
  companyEmail: string;
  billingEmail: string;
}

/**
 * `ean` and `direction` are currently required by the handler but never read (it
 * queries on `company_id` alone); PR A makes them optional. They are sent as
 * harmless placeholders so this works against today's backend too.
 */
export const fetchDestinationReadiness = async (
  destinationCompanyId: number,
  token: string,
  probe: { ean: string; direction: "injection" | "offtake" | "combined" }
): Promise<DestinationReadiness> => {
  const { data } = await axios.get(`${config.API_SERVICES}/api/v1/contracts/billing-info`, {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      company_id: destinationCompanyId,
      ean: probe.ean,
      direction: probe.direction,
    },
  });
  const body = (data ?? {}) as {
    hasBilling?: boolean;
    companyEmail?: string;
    billingEmail?: string;
  };
  return {
    hasBilling: typeof body.hasBilling === "boolean" ? body.hasBilling : undefined,
    companyEmail: (body.companyEmail ?? "").trim(),
    billingEmail: (body.billingEmail ?? "").trim(),
  };
};
